// A closed circuit described by a centreline with track widths, in the same
// layout as the TUMFTM racetrack-database: x east, z north (metres), with
// widths to the right/left of the driving direction.

export interface CentrelinePoint {
  x: number;
  z: number;
  widthRight: number;
  widthLeft: number;
}

export interface TrackProjection {
  /** Distance along the centreline from the start line, metres, in [0, length). */
  s: number;
  /** Signed lateral offset from the centreline, metres. Positive = right of driving direction. */
  offset: number;
  /** Index of the segment start point. */
  index: number;
  widthRight: number;
  widthLeft: number;
}

export interface TrackFrame {
  x: number;
  z: number;
  /** Unit tangent (driving direction). */
  tx: number;
  tz: number;
  /** Heading in radians, 0 = +z (north), positive towards +x (east). */
  heading: number;
  /** Elevation, metres (0 on tracks without elevation data). */
  y: number;
  /** Gradient along the driving direction (rise over run; positive = uphill). */
  slope: number;
  widthRight: number;
  widthLeft: number;
}

export class Track {
  readonly points: CentrelinePoint[];
  /** Cumulative distance at each point; cum[n] = total length. */
  private readonly cum: number[];
  readonly length: number;

  /** Elevation per centreline point, metres, or undefined for a flat track. */
  readonly elevation: readonly number[] | undefined;

  constructor(points: CentrelinePoint[], elevation?: readonly number[]) {
    if (points.length < 3) throw new Error('Track needs at least 3 points');
    if (elevation && elevation.length !== points.length) throw new Error('Elevation must have one value per centreline point');
    this.points = points;
    this.elevation = elevation;
    this.cum = [0];
    let total = 0;
    for (let i = 0; i < points.length; i++) {
      const a = points[i]!;
      const b = points[(i + 1) % points.length]!;
      total += Math.hypot(b.x - a.x, b.z - a.z);
      this.cum.push(total);
    }
    this.length = total;
  }

  get count(): number {
    return this.points.length;
  }

  private pt(i: number): CentrelinePoint {
    const n = this.points.length;
    return this.points[((i % n) + n) % n]!;
  }

  /** Position, direction and widths at distance s along the centreline. */
  frameAt(s: number): TrackFrame {
    const L = this.length;
    const ss = ((s % L) + L) % L;
    // Binary search the segment containing ss.
    let lo = 0;
    let hi = this.points.length - 1;
    while (lo < hi) {
      const mid = (lo + hi + 1) >> 1;
      if (this.cum[mid]! <= ss) lo = mid;
      else hi = mid - 1;
    }
    const a = this.pt(lo);
    const b = this.pt(lo + 1);
    const segLen = this.cum[lo + 1]! - this.cum[lo]!;
    const t = segLen > 0 ? (ss - this.cum[lo]!) / segLen : 0;
    const tx = (b.x - a.x) / (segLen || 1);
    const tz = (b.z - a.z) / (segLen || 1);
    return {
      x: a.x + (b.x - a.x) * t,
      z: a.z + (b.z - a.z) * t,
      tx,
      tz,
      heading: Math.atan2(tx, tz),
      widthRight: a.widthRight + (b.widthRight - a.widthRight) * t,
      widthLeft: a.widthLeft + (b.widthLeft - a.widthLeft) * t,
      ...this.heightAt(lo, t, segLen),
    };
  }

  private heightAt(i: number, t: number, segLen: number): { y: number; slope: number } {
    const e = this.elevation;
    if (!e) return { y: 0, slope: 0 };
    const n = e.length;
    const ya = e[((i % n) + n) % n]!;
    const yb = e[(((i + 1) % n) + n) % n]!;
    return { y: ya + (yb - ya) * t, slope: segLen > 0 ? (yb - ya) / segLen : 0 };
  }

  /**
   * Ground height near a world position: the elevation of the nearest
   * centreline point (0 on flat tracks). Pass the returned index as a hint
   * when sampling many nearby points in sequence.
   */
  heightNear(x: number, z: number, hint?: number): { y: number; index: number } {
    if (!this.elevation) return { y: 0, index: hint ?? 0 };
    const p = this.project(x, z, hint);
    return { y: this.frameAt(p.s).y, index: p.index };
  }

  /** Elevation at distance s along the lap, metres. */
  elevationAt(s: number): number {
    return this.elevation ? this.frameAt(s).y : 0;
  }

  /**
   * Project a world position onto the centreline. Pass the previous result's
   * index as a hint to search locally (fast); without a hint, searches all.
   */
  project(x: number, z: number, hintIndex?: number): TrackProjection {
    const n = this.points.length;
    let best = { d2: Infinity, i: 0, t: 0 };
    const check = (i: number): void => {
      const a = this.pt(i);
      const b = this.pt(i + 1);
      const dx = b.x - a.x;
      const dz = b.z - a.z;
      const len2 = dx * dx + dz * dz || 1;
      const t = Math.max(0, Math.min(1, ((x - a.x) * dx + (z - a.z) * dz) / len2));
      const px = a.x + dx * t - x;
      const pz = a.z + dz * t - z;
      const d2 = px * px + pz * pz;
      if (d2 < best.d2) best = { d2, i: ((i % n) + n) % n, t };
    };
    if (hintIndex === undefined) {
      for (let i = 0; i < n; i++) check(i);
    } else {
      const window = 40;
      for (let k = -window; k <= window; k++) check(hintIndex + k);
      // Fall back to a full search if the local result looks wrong (e.g. after a reset).
      if (best.d2 > 100 * 100) for (let i = 0; i < n; i++) check(i);
    }
    const a = this.pt(best.i);
    const b = this.pt(best.i + 1);
    const segLen = this.cum[best.i + 1]! - this.cum[best.i]!;
    const tx = (b.x - a.x) / (segLen || 1);
    const tz = (b.z - a.z) / (segLen || 1);
    const px = a.x + (b.x - a.x) * best.t;
    const pz = a.z + (b.z - a.z) * best.t;
    // Right-hand normal of the driving direction is (tz, -tx).
    const offset = (x - px) * tz + (z - pz) * -tx;
    return {
      s: this.cum[best.i]! + segLen * best.t,
      offset,
      index: best.i,
      widthRight: a.widthRight + (b.widthRight - a.widthRight) * best.t,
      widthLeft: a.widthLeft + (b.widthLeft - a.widthLeft) * best.t,
    };
  }
}

/** Grey-box test circuit: two straights joined by semicircles, driven anticlockwise (left turns). */
export function makeOval(straight = 600, radius = 120, width = 15, spacing = 5): Track {
  const pts: CentrelinePoint[] = [];
  const half = width / 2;
  const push = (x: number, z: number): void => {
    pts.push({ x, z, widthRight: half, widthLeft: half });
  };
  const nStraight = Math.round(straight / spacing);
  const nArc = Math.round((Math.PI * radius) / spacing);
  // Right-hand straight heading north, at x = +radius.
  for (let i = 0; i < nStraight; i++) push(radius, -straight / 2 + (i * straight) / nStraight);
  // Top semicircle, centre (0, +straight/2), clockwise from east to west.
  for (let i = 0; i < nArc; i++) {
    const a = (i / nArc) * Math.PI;
    push(radius * Math.cos(a), straight / 2 + radius * Math.sin(a));
  }
  // Left straight heading south, at x = -radius.
  for (let i = 0; i < nStraight; i++) push(-radius, straight / 2 - (i * straight) / nStraight);
  // Bottom semicircle back to the start.
  for (let i = 0; i < nArc; i++) {
    const a = Math.PI + (i / nArc) * Math.PI;
    push(radius * Math.cos(a), -straight / 2 + radius * Math.sin(a));
  }
  return new Track(pts);
}
